import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigService } from '../../../commons';
import { GenericResponse, IPaginated } from '../../../interfaces';
import {
  IncidenceDetailId,
  InstallationsNameCups,
  InvoiceRequestBody,
  SocialReason,
  FailureBody,
  ChangeCnaeBody,
  ChangePotencyBody,
  BankChargeBody,
  IncidenceDetailGeneric,
  BillingAddressBody,
  BankAccountBody,
  InstallationsBySocialReason,
  InvoiceEmittedBody,
  ReasonCessation,
  EmitInvoiceBody,
  ActivityCessationBody,
  SurrogateContractBody,
  InfoInvoiceRequestBody,
  ContactInformationBody,
  SignedContractBody,
  PaymentRequestBody,
  InvoicesBySocialReason,
  InvoicesByInstallation,
  CodePostal,
  TypeRoad,
  IncidenceList,
  UpdateNotificationsBody,
  OtherArrangementsType,
  OtherArrangements,
  DataNotifications,
  GetFilterSocialReasonsOptionsBody,
  FilterSocialReasonsOption,
  GetFilterOptionsBody,
  FilterCupsOption,
  FilterProcessOption,
  FilterCaseCodeOption,
  SelfConsumptionBody,
  SelfConsumptionSelectResponse,
} from '../models/incidents.model';

@Injectable({
  providedIn: 'root'
})
export class IncidenceService {
  public incidenceDetail: IncidenceDetailId;
  private createIncidence = new BehaviorSubject<boolean>(false);
  public casesNotifications = new BehaviorSubject<DataNotifications>(null);

  constructor(private http: HttpClient, private config: ConfigService) { }

  public setIdIncidenceDetail(params: IncidenceDetailId): void {
    this.incidenceDetail = params;
  }

  public getSendIncidence(): BehaviorSubject<boolean> {
    return this.createIncidence;
  }

  public setSendIncidence(data: boolean): void {
    this.createIncidence.next(data);
  }

  public getIncidences(params: any): Observable<GenericResponse<IPaginated<IncidenceList>>> {
    return this.http.get<GenericResponse<IPaginated<IncidenceList>>>(this.config.getUrl('incidences.incidence'), { params });
  }

  public getIncidencesHistorical(params: any): Observable<GenericResponse<IPaginated<IncidenceList>>> {
    return this.http.get<GenericResponse<IPaginated<IncidenceList>>>(this.config.getUrl('incidences.incidenceHistorical'), { params });
  }

  public getIncidencesHistoricalDetail(id: string): Observable<any> {
    return this.http.get<GenericResponse>(this.config.getUrl('incidences.incidenceHistoricalDetail', { idIncidencia: id }));
  }

  public incidenceUpdate(codIncidencia: string, params: any): Observable<any> {
    return this.http.put(this.config.getUrl('incidences.incidenceUpdate', { codIncidencia }), params);
  }

  public incidenceClose(codIncidencia: string, params: any): Observable<any> {
    return this.http.put(this.config.getUrl('incidences.incidenceClose', { codIncidencia }), params);
  }

  public postAttachedIncidenceFile(codIncidencia: string, params: any): Observable<any> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.attachFile', { codIncidencia }), params);
  }

  public getSocialReasons(params: any): Observable<GenericResponse<IPaginated<SocialReason>>> {
    if (!params.Filter) {
      delete params.Filter;
    }
    return this.http.get<GenericResponse<IPaginated<SocialReason>>>(this.config.getUrl('maestros.getSocialReasons'), { params });
  }

  public getPostalCode(codPostal: string): Observable<GenericResponse<CodePostal>> {
    return this.http.get<GenericResponse<CodePostal>>(this.config.getUrl('maestros.getCodigoPostal', { codPostal }));
  }

  public getBillsInstallation(id: number): Observable<GenericResponse<IPaginated<InvoicesByInstallation>>> {
    return this.http.get<GenericResponse<IPaginated<InvoicesByInstallation>>>(
      this.config.getUrl('pymes.customers.getBills', { idInstalacion: id }), { params: { PageNumber: '1', NumOfElements: '1000' } }
    );
  }

  public getBillsSocialReason(id: number): Observable<GenericResponse<IPaginated<InvoicesBySocialReason>>> {
    return this.http.get<GenericResponse<IPaginated<InvoicesBySocialReason>>>(this.config.getUrl('grandesCuentas.customers.getBills',
      { idRazonSocial: id }), { params: { PageNumber: '1', NumOfElements: '1000' } });
  }

  public getRoadType(): Observable<GenericResponse<TypeRoad[]>> {
    return this.http.get<GenericResponse<TypeRoad[]>>(this.config.getUrl('maestros.getTipoVia'));
  }

  public getInvoiceDetail(url: string, codIncidencia: string): Observable<GenericResponse<IncidenceDetailGeneric>> {
    return this.http.get<GenericResponse<IncidenceDetailGeneric>>(this.config.getUrl(url, { codIncidencia }));
  }

  public getInstallations(params: any, socialReason: boolean): Observable<
    GenericResponse<IPaginated<InstallationsBySocialReason | InstallationsNameCups>>
  > {
    if (!params.Filter) {
      delete params.Filter;
    }
    const url = socialReason ? 'maestros.getInstallationsBySocialReason' : 'maestros.getInstallationsByNameCups';
    return this.http.get<GenericResponse<IPaginated<InstallationsNameCups>>>(
      this.config.getUrl(url), { params }
    );
  }

  public getReasonCessation(): Observable<GenericResponse<ReasonCessation[]>> {
    return this.http.get<GenericResponse<ReasonCessation[]>>(this.config.getUrl('maestros.getReasonCessation'));
  }

  public getNotificationsCases(): Observable<GenericResponse<DataNotifications>> {
    return this.http.get<GenericResponse<DataNotifications>>(this.config.getUrl('incidences.getNotificationsCases'));
  }

  public postInvoiceRequest(params: InvoiceRequestBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postInvoiceRequest'), params);
  }

  public postPaymentRequest(params: PaymentRequestBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postPaymentRequest'), params);
  }

  public postChangePotency(params: ChangePotencyBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postChangePotency'), params);
  }

  public postIncidenceFailure(params: FailureBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postFailure'), params);
  }

  public postIncidenceChangeCnae(params: ChangeCnaeBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postChangeCnae'), params);
  }

  public postBankCharge(params: BankChargeBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postBankCharge'), params);
  }

  public postInvoiceConceptEmittedBody(params: InvoiceEmittedBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postInvoiceConceptEmitted'), params);
  }

  public postInvoiceEmittedBody(params: InvoiceEmittedBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postInvoiceEmitted'), params);
  }

  public postInvoicePowerEmittedBody(params: InvoiceEmittedBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postInvoicePowerEmitted'), params);
  }

  public postBillingAddress(params: BillingAddressBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postBillingAddress'), params);
  }

  public postBankAccount(params: BankAccountBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postBankAccount'), params);
  }

  public postIncidenceEmitInvoice(params: EmitInvoiceBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postEmitInvoice'), params);
  }

  public postActivityCessation(params: ActivityCessationBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postActivityCessation'), params);
  }

  public postSurrogateContract(params: SurrogateContractBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postSurrogateContract'), params);
  }

  public postInfoInvoiceRequest(params: InfoInvoiceRequestBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postInfoInvoiceRequest'), params);
  }

  public postContactInformation(params: ContactInformationBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postContactInformation'), params);
  }

  public postSignedContract(params: SignedContractBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postSignedContract'), params);
  }

  public putUpdateNotifications(params: UpdateNotificationsBody): Observable<GenericResponse> {
    return this.http.put<GenericResponse>(this.config.getUrl('incidences.putUpdateNotifications'), params);
  }

  public getFileIncident(name: string): Observable<any> {
    return this.http.get(this.config.getUrl(name), {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public downloadExcel(url: string, params: any): Observable<any> {
    return this.http.get(this.config.getUrl(url), {
      params,
      headers: { Accept: 'application/xls' },
      responseType: 'blob',
      observe: 'response'
    });
  }

  public getOtherArrangementsType(): Observable<GenericResponse<OtherArrangementsType[]>> {
    return this.http.get<GenericResponse<OtherArrangementsType[]>>(
      this.config.getUrl('incidences.getOtherArrangementsType')
    );
  }

  public postOtherArrangements(params: OtherArrangements): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postOtherArrangements'), params);
  }

  public getFilterSocialReasonsOptions(
    params: GetFilterSocialReasonsOptionsBody
  ): Observable<GenericResponse<IPaginated<FilterSocialReasonsOption>>> {
    if (!params.filter) {
      delete params.filter;
    }
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getFilterSocialReasonsOptions'), { params: (params as any) });
  }

  public getFilterCupsOptions(
    params: GetFilterOptionsBody
  ): Observable<GenericResponse<IPaginated<FilterCupsOption>>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getFilterCupsOptions'), { params: (params as any) });
  }

  public getFilterProcessOptions(language: string): Observable<GenericResponse<FilterProcessOption[]>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getFilterProcessOptions', { idioma: language }));
  }

  public getFilterCaseCodeOptions(
    params: GetFilterOptionsBody, url: string
  ): Observable<GenericResponse<IPaginated<FilterCaseCodeOption>>> {
    return this.http.get<GenericResponse>(this.config.getUrl(url), { params: (params as any) });
  }

  public getSelfConsumptionTypeOptions(): Observable<GenericResponse<SelfConsumptionSelectResponse>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getSelfConsumptionType'));
  }

  public getInstallationTypeOptions(): Observable<GenericResponse<SelfConsumptionSelectResponse>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getInstallationType'));
  }

  public getSubsectionTypeOptions(): Observable<GenericResponse<SelfConsumptionSelectResponse>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getSubsectionType'));
  }

  public getCupsTypeOptions(): Observable<GenericResponse<SelfConsumptionSelectResponse>> {
    return this.http.get<GenericResponse>(this.config.getUrl('maestros.getCupsType'));
  }

  public postSelfConsumption(params: SelfConsumptionBody): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(this.config.getUrl('incidences.postSelfConsumption'), params);
  }
}
